// Drag-to-scroll as a pure state machine.
//
// A gesture has three phases. It starts `pressed` on a mouse button going down
// and only becomes `dragging` once the pointer has travelled far enough to be
// unmistakable - below that it is still an ordinary click on whatever sits
// under the cursor. Keeping the two apart is what lets a carousel be dragged
// without breaking the links inside it.
//
// Everything here is pure, so the transitions can be tested without a DOM. The
// component owns the pointer listeners and the scrolling itself.

// The slack a click is allowed before it counts as a drag.
export const DRAG_THRESHOLD_PX = 5;

const toFinite = value => (Number.isFinite(value) ? value : 0);

export const idle = () => ({ phase: 'idle', startX: 0, startScrollLeft: 0 });

/**
 * The state a pointerdown leaves the gesture in.
 *
 * Only a primary mouse button starts one. Touch and pen are left to the
 * browser, whose own scrolling has momentum that nothing reimplemented in JS
 * would match. A row that already fits on one page has nowhere to be dragged.
 *
 * An origin that is not a real number is refused outright rather than coerced:
 * a gesture anchored at a made-up point would read its first real move as a
 * jump of several hundred pixels.
 */
export const press = ({
  pointerType,
  button,
  clientX,
  scrollLeft,
  canScroll,
} = {}) => {
  if (pointerType !== 'mouse' || button !== 0 || !canScroll) return idle();
  if (!Number.isFinite(clientX) || !Number.isFinite(scrollLeft)) return idle();

  return { phase: 'pressed', startX: clientX, startScrollLeft: scrollLeft };
};

/**
 * The next state for a pointermove.
 *
 * Returns the *same* state object whenever nothing has changed, so the
 * component can feed this straight into React state without re-rendering the
 * row on every pixel of travel.
 */
export const move = (state, { clientX, buttons } = {}) => {
  if (!state || state.phase === 'idle') return idle();

  // No button is down any more, and this page never saw it go up: the release
  // happened off-window or over browser chrome. Without this a gesture that
  // leaves the window would never end.
  if (buttons === 0) return idle();

  if (state.phase === 'dragging') return state;

  if (Math.abs(clientX - state.startX) < DRAG_THRESHOLD_PX) return state;

  return { ...state, phase: 'dragging' };
};

/** Every gesture ends here, however it went. */
export const release = () => idle();

export const isPressed = state => Boolean(state) && state.phase !== 'idle';

export const isDragging = state => Boolean(state) && state.phase === 'dragging';

/**
 * Where the row should sit for a pointer now at `clientX`.
 *
 * The delta is subtracted so the content follows the hand: dragging left pulls
 * later cards into view, which means a larger scroll offset.
 */
export const scrollLeftDuring = (state, clientX) => {
  const start = toFinite(state?.startScrollLeft);
  if (!Number.isFinite(clientX)) return start;

  return start - (clientX - toFinite(state?.startX));
};
